import type { GeneratedRegistryPersistence } from "./cogo-xyz-project-storage";
import type { StudioProjectSettingsRevisionRef } from "@/services/project-api";
import {
  isCogoXyzRegistrySnapshot,
  type CogoXyzRegistrySnapshot,
} from "./cogo-xyz-registry";

const STATE_SCHEMA = "cogo.cogo-xyz-registry-sync.v1";
const STORAGE_PREFIX = "cogo-studio:cogo-xyz-registry-sync:v1:";
const LOCK_PREFIX = "cogo-studio:cogo-xyz-registry-sync:";

export const COGO_XYZ_REGISTRY_SYNC_AUTHORITY = {
  browser: "web-locks-when-available",
  pendingDurability: "same-origin-local-storage",
  crossDevice: "server-cas-required",
} as const;

export interface RegistrySyncStorage {
  getItem(key: string): string | null;
  setItem(key: string, value: string): void;
  removeItem(key: string): void;
}

export interface RegistrySyncLockManager {
  request<T>(
    name: string,
    options: { mode: "exclusive" },
    callback: () => Promise<T>,
  ): Promise<T>;
}

type PendingSnapshot = {
  generation: number;
  snapshot: CogoXyzRegistrySnapshot;
  expectedSettingsRevision?: StudioProjectSettingsRevisionRef;
};

type DurableProjectSyncState = {
  schema: typeof STATE_SCHEMA;
  projectId: string;
  generation: number;
  pending?: PendingSnapshot;
};

const fallbackLockTails = new Map<string, Promise<void>>();

async function withFallbackLock<T>(
  name: string,
  callback: () => Promise<T>,
): Promise<T> {
  const previous = fallbackLockTails.get(name) ?? Promise.resolve();
  let release!: () => void;
  const current = new Promise<void>((resolve) => {
    release = resolve;
  });
  const tail = previous.catch(() => undefined).then(() => current);
  fallbackLockTails.set(name, tail);
  await previous.catch(() => undefined);
  try {
    return await callback();
  } finally {
    release();
    if (fallbackLockTails.get(name) === tail) fallbackLockTails.delete(name);
  }
}

function defaultStorage(): RegistrySyncStorage {
  if (typeof localStorage === "undefined") {
    throw new Error("COGO_XYZ_REGISTRY_SYNC_STORAGE_UNAVAILABLE");
  }
  return localStorage;
}

function defaultLockManager(): RegistrySyncLockManager | null {
  if (typeof navigator === "undefined") return null;
  const locks = (
    navigator as Navigator & { locks?: RegistrySyncLockManager }
  ).locks;
  return locks ?? null;
}

function isDurableState(
  value: unknown,
  projectId: string,
): value is DurableProjectSyncState {
  if (!value || typeof value !== "object") return false;
  const state = value as Partial<DurableProjectSyncState>;
  if (
    state.schema !== STATE_SCHEMA ||
    state.projectId !== projectId ||
    !Number.isSafeInteger(state.generation) ||
    (state.generation ?? -1) < 0
  ) {
    return false;
  }
  if (state.pending === undefined) return true;
  return (
    typeof state.pending === "object" &&
    Number.isSafeInteger(state.pending.generation) &&
    state.pending.generation > 0 &&
    state.pending.generation <= state.generation! &&
    isCogoXyzRegistrySnapshot(state.pending.snapshot) &&
    (state.pending.expectedSettingsRevision === undefined ||
      (state.pending.expectedSettingsRevision.authority ===
        "studio-project-settings-server" &&
        typeof state.pending.expectedSettingsRevision.value === "string" &&
        state.pending.expectedSettingsRevision.value.length > 0))
  );
}

export class CogoXyzRegistrySyncCoordinator {
  private readonly storageOverride?: RegistrySyncStorage;
  private readonly locksOverride?: RegistrySyncLockManager | null;

  constructor(options?: {
    storage?: RegistrySyncStorage;
    locks?: RegistrySyncLockManager | null;
  }) {
    this.storageOverride = options?.storage;
    this.locksOverride = options?.locks;
  }

  async saveSnapshot(
    projectId: string,
    _snapshot: CogoXyzRegistrySnapshot,
    save: () => Promise<void>,
  ): Promise<void> {
    await this.withProjectLock(projectId, async () => {
      const state = this.readState(projectId);
      await save();
      this.writeState(projectId, {
        schema: STATE_SCHEMA,
        projectId,
        generation: state.generation + 1,
      });
    });
  }

  async persistGeneratedSnapshot(
    projectId: string,
    snapshot: CogoXyzRegistrySnapshot,
    persist: () => Promise<GeneratedRegistryPersistence>,
  ): Promise<GeneratedRegistryPersistence> {
    return this.withProjectLock(projectId, async () => {
      const state = this.readState(projectId);
      const generation = state.generation + 1;
      const persistence = await persist();
      this.writeState(projectId, {
        schema: STATE_SCHEMA,
        projectId,
        generation,
        ...(persistence.status === "sync_pending"
          ? {
              pending: {
                generation,
                snapshot,
                ...(persistence.expectedSettingsRevision
                  ? {
                      expectedSettingsRevision:
                        persistence.expectedSettingsRevision,
                    }
                  : {}),
              },
            }
          : {}),
      });
      return persistence;
    });
  }

  async retryPendingSnapshot(
    projectId: string,
    persist: (
      snapshot: CogoXyzRegistrySnapshot,
      expectedSettingsRevision: StudioProjectSettingsRevisionRef,
    ) => Promise<GeneratedRegistryPersistence>,
  ): Promise<GeneratedRegistryPersistence> {
    return this.withProjectLock(projectId, async () => {
      const state = this.readState(projectId);
      if (!state.pending) {
        throw new Error(`COGO_XYZ_REGISTRY_SYNC_NOT_PENDING:${projectId}`);
      }
      if (!state.pending.expectedSettingsRevision) {
        throw new Error(
          `COGO_XYZ_REGISTRY_SYNC_SETTINGS_REVISION_UNAVAILABLE:${projectId}`,
        );
      }
      const persistence = await persist(
        state.pending.snapshot,
        state.pending.expectedSettingsRevision,
      );
      if (persistence.status === "saved") {
        this.writeState(projectId, {
          schema: STATE_SCHEMA,
          projectId,
          generation: state.generation,
        });
      }
      return persistence;
    });
  }

  private async withProjectLock<T>(
    projectId: string,
    callback: () => Promise<T>,
  ): Promise<T> {
    const name = `${LOCK_PREFIX}${encodeURIComponent(projectId)}`;
    const locks = this.locksOverride ?? defaultLockManager();
    if (locks) return locks.request(name, { mode: "exclusive" }, callback);
    return withFallbackLock(name, callback);
  }

  private readState(projectId: string): DurableProjectSyncState {
    const storage = this.storageOverride ?? defaultStorage();
    const raw = storage.getItem(this.storageKey(projectId));
    if (raw === null) {
      return { schema: STATE_SCHEMA, projectId, generation: 0 };
    }
    let parsed: unknown;
    try {
      parsed = JSON.parse(raw);
    } catch {
      throw new Error(`COGO_XYZ_REGISTRY_SYNC_STATE_INVALID:${projectId}`);
    }
    if (!isDurableState(parsed, projectId)) {
      throw new Error(`COGO_XYZ_REGISTRY_SYNC_STATE_INVALID:${projectId}`);
    }
    return parsed;
  }

  private writeState(
    projectId: string,
    state: DurableProjectSyncState,
  ): void {
    const storage = this.storageOverride ?? defaultStorage();
    storage.setItem(this.storageKey(projectId), JSON.stringify(state));
  }

  private storageKey(projectId: string): string {
    return `${STORAGE_PREFIX}${encodeURIComponent(projectId)}`;
  }
}

export const cogoXyzRegistrySyncCoordinator =
  new CogoXyzRegistrySyncCoordinator();
